import React, { useState, useEffect, useRef } from "react";
import {
  Home,
  BaggageClaim,
  Van,
  BookHeart,
  User,
  LogIn, // Added LogIn icon
} from "lucide-react";
import "./Navbar.css";
import { useNavigate } from "react-router-dom";

// 1. Define the Props Interface correctly
interface NavbarProps {
  isLoggedIn: boolean;
}

interface NavItem {
  id: string;
  label: string;
  icon: React.ReactNode;
}

const Navbar: React.FC<NavbarProps> = ({ isLoggedIn }) => {
  const [activeIndex, setActiveIndex] = useState<number>(0);
  const [showLabelIndex, setShowLabelIndex] = useState<number | null>(null);
  const navigate = useNavigate();
  const linkHeight = 56;
  const hideTimeoutRef = useRef<number | null>(null);

  // 2. Compute available items based on session
  const filteredNavItems: NavItem[] = isLoggedIn
    ? [
        { id: "", label: "Home", icon: <Home size={20} /> },
        { id: "cart", label: "Cart", icon: <BaggageClaim size={20} /> },
        { id: "orders", label: "Order", icon: <Van size={20} /> },
        { id: "favourite", label: "Favourite", icon: <BookHeart size={20} /> },
        { id: "profile", label: "Profile", icon: <User size={20} /> },
      ]
    : [
        { id: "", label: "Home", icon: <Home size={20} /> },
        { id: "auth", label: "Login", icon: <LogIn size={20} /> },
      ];

  // Handle global click to hide label
  useEffect(() => {
    const handleClickOutside = () => setShowLabelIndex(null);
    document.addEventListener("click", handleClickOutside);
    return () => document.removeEventListener("click", handleClickOutside);
  }, []);

  const handleItemClick = (index: number, e: React.MouseEvent) => {
    e.stopPropagation();
    setActiveIndex(index);
    setShowLabelIndex(index);

    if (hideTimeoutRef.current) {
      clearTimeout(hideTimeoutRef.current);
    }

    navigate(`/${filteredNavItems[index].id}`);

    hideTimeoutRef.current = window.setTimeout(() => {
      setShowLabelIndex(null);
      hideTimeoutRef.current = null;
    }, 1000);
  };

  return (
    <nav className="navbar-gooey">
      <ul className="navbar__menu">
        {filteredNavItems.map((item, index) => {
          const isActive = index === activeIndex;
          const showLabel = index === showLabelIndex;
          return (
            <li
              key={item.id || "home"} // Fixed key for empty string id
              className="navbar__item"
              onClick={(e) => handleItemClick(index, e)}
            >
              <div className={`navbar__link ${isActive ? "active" : ""}`}>
                {item.icon}
                <span
                  style={{
                    opacity: showLabel ? 1 : 0,
                    pointerEvents: "none", // Prevent span from blocking clicks
                  }}
                >
                  {item.label}
                </span>
              </div>
            </li>
          );
        })}

        {/* Gooey indicator follows active item height */}
        <div
          className="gooey-indicator visible"
          style={{
            transform: `translateY(${activeIndex * linkHeight}px)`,
          }}
        />
      </ul>
    </nav>
  );
};

export default Navbar;
